import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import { careerRoadmap } from '../../data/careerRoadmap';
import { courses } from '../../data/courses';

const stageIcons = {
  build: RocketLaunchOutlinedIcon,
  solve: PsychologyOutlinedIcon,
  design: HubOutlinedIcon,
  ai: AutoAwesomeOutlinedIcon
};

const benefits = [
  { icon: RouteOutlinedIcon, title: 'Learn in the order you are interviewed', text: 'No guesswork about what to study next. Every stage maps to a real interview round.' },
  { icon: WorkspacePremiumOutlinedIcon, title: 'Proof, not certificates', text: 'Leave with projects and designs you can walk an interviewer through.' },
  { icon: SupervisorAccountOutlinedIcon, title: 'Mentors who have hired', text: 'Feedback from engineers who sit on the other side of the interview table.' }
];

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const CareerGrowth = ({ onCourseClick }) => {
  const [active, setActive] = useState(0);
  // Auto-advance through the stages until the visitor picks one themselves.
  const [autoPlay, setAutoPlay] = useState(true);
  const [paused, setPaused] = useState(false);
  const stage = careerRoadmap[active];
  const last = careerRoadmap.length - 1;

  useEffect(() => {
    if (prefersReducedMotion()) setAutoPlay(false);
  }, []);

  const hoverTimer = useRef(null);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const pick = (i) => {
    clearTimeout(hoverTimer.current);
    setActive(i);
    setAutoPlay(false);
  };

  // Hovering selects a stage after a short pause, so sweeping the mouse
  // across stages on the way to the details panel doesn't switch them.
  const hoverStage = (i) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => pick(i), 150);
  };
  const cancelHover = () => clearTimeout(hoverTimer.current);

  const openCourse = (title) => {
    const course = courses.find((c) => c.title === title);
    if (course && onCourseClick) onCourseClick(course);
  };

  return (
    <section className="home-section career" id="career-growth">
      <div className="container">
        <div className="home-section-head" data-reveal>
          <span className="section-eyebrow">Career growth</span>
          <h2>
            Get hired by <span className="text-gradient">product companies &amp; AI startups</span>
          </h2>
          <p>
            Top teams hire for skills they can test. We build them in the order interviews test them, so every week
            of learning moves you closer to a bigger offer.
          </p>
        </div>

        <div
          className={`rm-panel ${paused ? 'is-paused' : ''}`}
          data-reveal
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          style={{ '--c1': stage.accent[0], '--c2': stage.accent[1] }}
        >
          <div className="rm-stages" role="tablist" aria-label="Career roadmap">
            <div className="rm-track" aria-hidden="true">
              <span style={{ width: `${(active / last) * 100}%` }} />
            </div>
            {careerRoadmap.map((s, i) => {
              const Icon = stageIcons[s.id];
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active === i}
                  className={`rm-stage ${active === i ? 'active' : ''} ${i < active ? 'done' : ''}`}
                  style={{ '--s1': s.accent[0], '--s2': s.accent[1] }}
                  onClick={() => pick(i)}
                  onMouseEnter={() => hoverStage(i)}
                  onMouseLeave={cancelHover}
                >
                  <span className="rm-stage-icon"><Icon /></span>
                  <span className="rm-stage-step">Stage {i + 1}</span>
                  <strong>{s.title}</strong>
                  <span className="rm-stage-tagline">{s.tagline}</span>
                  {autoPlay && active === i && (
                    <span
                      className="rm-stage-progress"
                      onAnimationEnd={() => setActive((a) => (a + 1) % careerRoadmap.length)}
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div className="rm-detail" key={stage.id} role="tabpanel">
            <div className="rm-outcome">
              <div className="rm-hiring">
                {stage.hiring.map((h) => <span key={h}>{h}</span>)}
              </div>
              <h3>{stage.outcome}</h3>
              <p><strong>What interviewers test:</strong> {stage.tests}</p>
            </div>

            <div className="rm-build">
              <h4>What you will build</h4>
              <ul>
                {stage.build.map((b, i) => (
                  <li key={b} style={{ '--i': i }}><CheckIcon sx={{ fontSize: 16 }} /> {b}</li>
                ))}
              </ul>
            </div>

            <div className="rm-next">
              <h4>Prepares you for</h4>
              <div className="rm-rounds">
                {stage.rounds.map((r) => <span key={r}>{r}</span>)}
              </div>
              <h4>Start with</h4>
              <div className="rm-courses">
                {stage.courses.map((title) => {
                  const course = courses.find((c) => c.title === title);
                  const Icon = course?.icon;
                  return (
                    <button type="button" key={title} onClick={() => openCourse(title)}>
                      {Icon && <span className="rm-course-icon" style={{ '--a1': course.accent[0], '--a2': course.accent[1] }}><Icon sx={{ fontSize: 16 }} /></span>}
                      {title}
                      <ArrowForwardIcon sx={{ fontSize: 15 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="rm-benefits">
          {benefits.map(({ icon: Icon, title, text }, i) => (
            <div className="rm-benefit spotlight" key={title} data-reveal style={{ '--i': i }}>
              <span className="rm-benefit-icon"><Icon /></span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rm-cta" data-reveal>
          <p>Not sure where you are on the roadmap? A mentor will map out your next step.</p>
          <Link to="/#book-session" className="btn-hero-primary">
            Book my roadmap call <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CareerGrowth;
